// Catches React render errors so a single broken subtree doesn't
// blank the whole configurator. Shows the error + stack inline so the
// user can copy it back to a developer instead of just seeing a blank
// page. Also re-logs to console so source-mapped traces land in
// devtools.
//
// Class component because React's error-boundary API is still
// class-only as of React 18.

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Short label for the area being protected, e.g. "Edit Financing". */
  label?: string;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface in devtools — production builds have source maps so the
    // stack will point at real source lines.
    console.error(
      `[ErrorBoundary${this.props.label ? ` · ${this.props.label}` : ""}]`,
      error,
      info,
    );
    this.setState({ componentStack: info.componentStack ?? null });
  }

  handleReset = () => this.setState({ error: null, componentStack: null });

  render() {
    if (!this.state.error) return this.props.children;

    const stack = this.state.error.stack ?? String(this.state.error);
    const compStack = this.state.componentStack ?? "(none)";

    return (
      <div className="my-4 border border-red-400 bg-red-50 p-4 font-sans text-sm text-red-900">
        <p className="mb-2 font-bold uppercase tracking-tag">
          Something broke
          {this.props.label ? ` in ${this.props.label}` : ""}.
        </p>
        <p className="mb-3">
          Copy the details below and send to your developer, then click
          "Reset" to keep using the rest of the page.
        </p>
        <details className="mb-3">
          <summary className="cursor-pointer font-semibold">
            Error details ({this.state.error.message})
          </summary>
          <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words rounded bg-white p-2 text-[12px] text-red-900">
            {stack}
            {"\n\n--- component stack ---\n"}
            {compStack}
          </pre>
        </details>
        <button
          type="button"
          onClick={this.handleReset}
          className="rounded-full bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800"
        >
          Reset
        </button>
      </div>
    );
  }
}
